import { ICommand } from 'core-ts-types';
import * as _4 from 'lodash4';
import * as moment from 'moment';
import { IPopupConfigService } from 'ui-ng-popup';
import {
  IAppointmentsStates,
  IAppointmentsTabsConfiguration,
  IAppointmentsTabsConfigurationDataType,
  IAppointmentsTabsConfigurationOptions
} from '../servants/appointments_tabs_configuration';

interface IStateObject {
  name: string;
  params?: any;
}

export default class MyAppointmentsStateService {
  static DEFAULT_ENTRY_STATE = 'my.myAppointments.upcoming';
  private _initState: string = MyAppointmentsStateService.DEFAULT_ENTRY_STATE;
  private _initParams: any = {};
  private _stateStack: IStateObject[] = [];
  private _invitationId?: string;
  private _transitionMutex: boolean = false;

  constructor(
    private $state: ng.ui.IStateService,
    private ngAppointmentsTabsConfiguration: IAppointmentsTabsConfiguration,
    private clearAllAppointmentsDataSourcesCommand: ICommand,
    private $timeout: ng.ITimeoutService,
    private ngPopupService: IPopupConfigService,
    private phxFocusDelegate: any,
    private phxAxReturnFocusFactory: any
  ) {}

  get initState(): string {
    return this._initState;
  }

  set initState(name: string) {
    this._initState = name;
  }

  get initParams(): any {
    return this._initParams;
  }

  set initParams(params: any) {
    this._initParams = params;
  }

  get tabs(): IAppointmentsTabsConfigurationDataType {
    return this.ngAppointmentsTabsConfiguration.read();
  }

  get invitationId() {
    return this._invitationId;
  }

  get currentStep(): IAppointmentsTabsConfigurationOptions {
    return _4.find(this.tabs, (tab: IAppointmentsTabsConfigurationOptions) =>
      _4.includes(this.$state.current.name as string, tab.state)
    );
  }

  get currentStepIndex() {
    const tabsValues = _4.values<IAppointmentsTabsConfigurationOptions>(this.tabs);
    return _4.findIndex(tabsValues, (tab) => _4.includes(this.$state.current.name as string, tab.state));
  }

  get states(): IAppointmentsStates {
    return this.ngAppointmentsTabsConfiguration.states();
  }

  stateLoaded() {
    this.$timeout(() => (this._transitionMutex = false), 500);
  }

  refresh() {
    this._invitationId = undefined;
    this._stateStack = [];
    this.clearAllAppointmentsDataSourcesCommand();
  }

  init() {
    const stateRegex = /(my\.myAppointments\.request)|(my\.appointments\.)/;
    const currentStateName = this.$state.current.name as string;
    this._transitionMutex = false;
    this.initState = !currentStateName.match(stateRegex)
      ? currentStateName || MyAppointmentsStateService.DEFAULT_ENTRY_STATE
      : MyAppointmentsStateService.DEFAULT_ENTRY_STATE;
    this.initParams = this.$state.params || {};
    this._invitationId = !!this.$state.params ? this.$state.params.invitationId : undefined;
  }

  go(nextName: string, nextParams?: any) {
    if (this._transitionMutex) {
      return;
    }
    this._transitionMutex = true;
    const { name } = this.$state.current;
    const params = this.$state.params;
    const nameIdx = _4.findIndex(this._stateStack, { name });
    if (nameIdx > -1) {
      this._stateStack.splice(3, this._stateStack.length);
    }
    if (!!name && name !== 'my.appointments') {
      this._stateStack.push({
        name,
        params
      });
    }
    this.$state
      .go(nextName, nextParams)
      .then(() => (this._transitionMutex = false))
      .catch(() => (this._transitionMutex = false));
  }

  skipAndGo(nextName: string, nextParams?: any, enforce?: boolean): void {
    if (this._transitionMutex && !enforce) {
      return;
    }
    this._transitionMutex = true;
    this.$state
      .go(nextName, nextParams)
      .then(() => (this._transitionMutex = false))
      .catch(() => (this._transitionMutex = false));
  }

  isCurrentState(state: string): boolean {
    return _4.includes(this.$state.current.name as string, state);
  }

  exit() {
    const self = this;
    this.ngPopupService.show({
      title: 'Discard Appointment?',
      body: 'Are you sure you want to exit? All selections will be reset.',
      completeButtonText: 'Discard Appointment',
      cancelButtonText: 'Keep Scheduling',
      completeCallback: () => {
        self.confirmExit();
      },
      cancelCallback: () => {
        this.phxFocusDelegate.focus(this.phxAxReturnFocusFactory.getFocusId());
      }
    });
  }

  confirmExit() {
    this.refresh();
    this.goToInitState();
    this.phxFocusDelegate.focus(this.phxAxReturnFocusFactory.getFocusId());
  }

  goToInitState() {
    this.$state.go(this.initState, this.initParams);
  }

  next() {
    if (!!this.currentStep && this.currentStep.nextState) {
      const name = this.currentStep.nextState;
      this.go(name);
    }
  }

  restartScheduling() {
    this.go(this.ngAppointmentsTabsConfiguration.states().reason);
  }

  goToWalkInTimes() {
    this.go(this.ngAppointmentsTabsConfiguration.states().walkIn);
  }

  goToAvailableTimes() {
    this.go(this.ngAppointmentsTabsConfiguration.states().availableTimes);
  }

  goToAlternateTimes() {
    this.go(this.ngAppointmentsTabsConfiguration.states().alternateTimes);
  }

  goToAlternateTimesConfirm() {
    this.go(this.ngAppointmentsTabsConfiguration.states().alternateTimesConfirm);
  }

  goToConfirm() {
    this.go(this.ngAppointmentsTabsConfiguration.states().confirm);
  }

  goToScheduled() {
    this.refresh();
    this.go(this.ngAppointmentsTabsConfiguration.states().scheduled);
  }

  back() {
    if (this._transitionMutex) {
      return;
    }
    this._transitionMutex = true;
    const previousState = this._stateStack.pop();
    if (!!previousState) {
      this.$state
        .go(previousState.name, previousState.params)
        .then(() => (this._transitionMutex = false))
        .catch(() => (this._transitionMutex = false));
    } else {
      this._transitionMutex = false;
      this.exit();
    }
  }

  checkState(): boolean {
    if (_4.isEmpty(this.$state.current.name)) {
      this.restartScheduling();
      return false;
    }
    return true;
  }

  showStepTabs(): boolean {
    const { alternateTimes, walkIn, alternateTimesConfirm, scheduled } = this.ngAppointmentsTabsConfiguration.states();
    return !(
      this.isCurrentState(alternateTimes) ||
      this.isCurrentState(walkIn) ||
      this.isCurrentState(alternateTimesConfirm) ||
      this.$state.current.name === scheduled
    );
  }

  get showFooterButton(): boolean {
    const { walkIn, scheduled } = this.ngAppointmentsTabsConfiguration.states();
    return !(this.isCurrentState(walkIn) || this.$state.current.name === scheduled);
  }

  get showHeaderButtons(): boolean {
    return this.$state.current.name !== this.ngAppointmentsTabsConfiguration.states().scheduled;
  }
}



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-appointments/src/services/my_appointment_state_service.ts